import React, { useState, useMemo } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';

import TableRowAction from 'components/common/TableRowAction';
import CustomAdvanceTable from 'components/common/advance-table/CustomAdvanceTable';
import ConfirmationModal from 'components/common/modals/ConfirmationModal';
import SoftBadge from 'components/common/SoftBadge';
import ViewFileButton from 'components/common/ViewFileButton';
import { permissionsKeyMap } from 'routes/permissions/permissionKeys';

import { useAuthContext } from 'context/authContext';
import useUpload from 'hooks/useUpload';
import {
  useBeneficiarySupportingDocs,
  useCreateBeneficiarySupportingDoc,
  useApproveBeneficiarySupportingDoc,
  useDeleteBeneficiarySupportingDoc
} from 'hooks/useBeneficiaries';

import SupportingDocsUploadModal from './SupportingDocsUploadModal';

const formatUserName = user =>
  user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '---';

const BeneficiarySupportingDocs = () => {
  const { id } = useParams();
  const { isGroundsTeamManager, user } = useAuthContext();

  const { documents, isLoading } = useBeneficiarySupportingDocs(id);
  const { createDocumentAsync, isCreating } =
    useCreateBeneficiarySupportingDoc();
  const { approveDocumentAsync, isApproving } =
    useApproveBeneficiarySupportingDoc();
  const { deleteDocumentAsync } = useDeleteBeneficiarySupportingDoc();
  const { uploadDocument, loading: isUploading } = useUpload();

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [approvingId, setApprovingId] = useState(null);

  const columns = useMemo(
    () => [
      {
        accessor: 'document_type',
        Header: 'Document Type',
        cellProps: { className: 'py-2' },
        Cell: rowData => (
          <span style={{ fontSize: '1rem' }}>
            {rowData.row.original.document_type}
          </span>
        )
      },
      {
        accessor: 'file_url',
        Header: 'File',
        cellProps: { className: 'py-2' },
        disableSortBy: true,
        Cell: rowData => <ViewFileButton url={rowData.row.original.file_url} />
      },
      {
        accessor: 'uploaded_by_user',
        Header: 'Uploaded By',
        cellProps: { className: 'py-2' },
        Cell: rowData => (
          <span style={{ fontSize: '1rem' }}>
            {formatUserName(rowData.row.original.uploaded_by_user)}
          </span>
        )
      },
      {
        accessor: 'created_at',
        Header: 'Uploaded',
        cellProps: { className: 'py-2' },
        Cell: rowData => (
          <span style={{ fontSize: '1rem' }}>
            {format(
              new Date(rowData.row.original.created_at),
              'dd-MM-yyyy, hh:mm a'
            )}
          </span>
        )
      },
      {
        accessor: 'approved',
        Header: 'Status',
        cellProps: { className: 'py-2' },
        Cell: rowData => {
          const row = rowData.row.original;
          if (row.approved) {
            return (
              <div>
                <SoftBadge pill bg="success">
                  Approved
                </SoftBadge>
                <div className="text-muted fs--1 mt-1">
                  by {formatUserName(row.approved_by_user)}
                </div>
              </div>
            );
          }
          return (
            <SoftBadge pill bg="warning">
              Pending
            </SoftBadge>
          );
        }
      },
      {
        accessor: 'id',
        Header: '',
        cellProps: { className: 'py-2 text-end' },
        disableSortBy: true,
        Cell: rowData => {
          const row = rowData.row.original;
          const canApproveThis = isGroundsTeamManager && !row.approved;
          return (
            <div className="d-flex gap-2 justify-content-end align-items-center">
              {canApproveThis && (
                <Button
                  size="sm"
                  variant="success"
                  disabled={isApproving && approvingId === row.id}
                  onClick={async () => {
                    setApprovingId(row.id);
                    try {
                      await approveDocumentAsync(row.id);
                    } finally {
                      setApprovingId(null);
                    }
                  }}
                >
                  {isApproving && approvingId === row.id
                    ? 'Approving...'
                    : 'Approve'}
                </Button>
              )}
              <TableRowAction
                onCLickDelete={() => setConfirmDeleteId(row.id)}
                subject={permissionsKeyMap.sponsorship.children.beneficiaries}
              />
            </div>
          );
        }
      }
    ],
    [isGroundsTeamManager, isApproving, approvingId, approveDocumentAsync]
  );

  const handleUpload = async ({ documentType, file }) => {
    try {
      const fileUrl = await uploadDocument(file, 'supporting-documents');
      await createDocumentAsync({
        orphan_id: id,
        document_type: documentType,
        file_url: fileUrl,
        file_name: file.name,
        file_size: file.size,
        uploaded_by: user?.id || null
      });
      setShowUploadModal(false);
    } catch (e) {
      // toast handled in hooks
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteDocumentAsync(confirmDeleteId);
      setConfirmDeleteId(null);
    } catch (e) {
      // toast handled in hook
    }
  };

  return (
    <>
      {showUploadModal && (
        <SupportingDocsUploadModal
          show={showUploadModal}
          onHide={() => setShowUploadModal(false)}
          onSubmit={handleUpload}
          loading={isCreating || isUploading}
        />
      )}
      {confirmDeleteId && (
        <ConfirmationModal
          show={!!confirmDeleteId}
          handleConfirm={handleConfirmDelete}
          onHide={() => setConfirmDeleteId(null)}
          title="Delete Document"
          body="Are you sure you want to delete this document?"
        />
      )}

      <Row>
        <Col md={12} xxl={12} className="h-md-100">
          <CustomAdvanceTable
            data={documents}
            columns={columns}
            title="Supporting Documents"
            showSearchInput
            searchInputPlaceHolder="Search"
            addNew
            addButtonLabel="Upload Doc"
            onClickAddNew={() => setShowUploadModal(true)}
            perPage={50}
            loading={isLoading}
            subject={permissionsKeyMap.sponsorship.children.beneficiaries}
          />
        </Col>
      </Row>
    </>
  );
};

export default BeneficiarySupportingDocs;
